import axios from "axios";
import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useHotelInfoContext } from "../../../../../components/HotelInfoContext";

export const usePaypal = ({
  bookingSidebar,
  navigate,
  currentUser,
  totalamount,
  totaldays,
  childrenAgeRule,
}) => {
  // Refs for guest information
  const guestNameRef = useRef("");
  const guestEmailRef = useRef("");
  const guestPhoneNumberRef = useRef("");
  const guestNationalityRef = useRef("");
  const specialRequestRef = useRef("");
  const [loading, setLoading] = useState(false);
  const { hotelInfo: ctxHotelInfo } = useHotelInfoContext() || {
    hotelInfo: null,
  };

  useEffect(() => {
    // Load PayPal SDK

    const loadPayPalSDK = () => {
      // Remove any existing PayPal script to avoid duplication
      const existingScript = document.querySelector(
        'script[src*="paypal.com/sdk/js"]'
      );
      if (existingScript) existingScript.remove();

      // Create the PayPal script dynamically
      const script = document.createElement("script");
      script.src = `https://www.paypal.com/sdk/js?client-id=AfYguK6owoLNLgN2lBJN3m8AST8zeX2swwYqcLHmV9WSy9z-7b7ibTDyVaRBICEa0bcXRtE66i_wvF1N&buyer-country=US&currency=USD&components=buttons&enable-funding=venmo,paylater,card`;
      script.setAttribute("data-sdk-integration-source", "developer-studio");

      script.addEventListener("load", () => {
        console.log("PayPal SDK loaded successfully.");
        if (window.paypal) {
          setUpPayPalButton();
        } else {
          Swal.fire(
            "Error",
            "PayPal SDK could not be loaded. Please try again later.",
            "error"
          );
        }
      });

      script.addEventListener("error", (err) => {
        console.error("Failed to load PayPal SDK:", err);
        Swal.fire(
          "Error",
          "PayPal SDK could not be loaded. Please try again later.",
          "error"
        );
      });

      document.body.appendChild(script);
    };
    loadPayPalSDK();

    return () => {
      const script = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (script) script.remove();
    };
  }, []);

  const setUpPayPalButton = () => {
    if (window.paypal) {
      console.log("Initializing PayPal Buttons...");
      window.paypal
        .Buttons({
          createOrder: async () => {
            const name = guestNameRef.current.trim();
            const email = guestEmailRef.current.trim();
            const phone = guestPhoneNumberRef.current.trim();
            const nationality = guestNationalityRef.current.trim();
            const request = specialRequestRef.current.trim();

            if (!name || !email || !phone || !nationality || !request) {
              Swal.fire(
                "Error",
                "All guest information fields are required.",
                "error"
              );
              throw new Error("Missing guest information.");
            }
            console.log("Creating PayPal order...");

            const roomPrice = bookingSidebar.reduce(
              (acc, item) =>
                acc +
                (item.type !== "addonservice"
                  ? item.totalRate * item.selectedRooms
                  : 0),
              0
            );
            const addOnServicePrice = bookingSidebar.reduce(
              (acc, item) =>
                acc + (item.type === "addonservice" ? item.servicePrice : 0),
              0
            );
            try {
              const response = await axios.post(
                "/api/payment/createPayPalOrder",
                {
                  totalAmount: totalamount,
                  currency: "VND",
                  bookingSidebar,
                  totalDays: totaldays,
                  breakdownDetails: { roomPrice, addOnServicePrice },
                }
              );
              console.log("Order ID:", response.data.orderId);
              return response.data.orderId;
            } catch (error) {
              console.error("Error creating PayPal order:", error);
              throw error;
            }
          },
          onApprove: async (data) => {
            console.log("PayPal approved:", data);
            try {
              await handlePayPalOrder(data.orderID);
            } catch (error) {
              console.error("Error completing PayPal booking:", error);
            }
          },
          onError: (err) => {
            console.error("PayPal button error:", err);
          },
        })
        .render("#paypal-button-container")
        .catch((err) => {
          console.error("Error rendering PayPal button:", err);
        });
    } else {
      console.error("PayPal SDK not ready.");
    }
  };

  const handlePayPalOrder = async (orderId) => {
    // Use refs to get guest information
    const name = guestNameRef.current.trim();
    const email = guestEmailRef.current.trim();
    const phone = guestPhoneNumberRef.current.trim();
    const nationality = guestNationalityRef.current.trim();
    const request = specialRequestRef.current.trim();

    if (!name || !email || !phone || !nationality || !request) {
      Swal.fire("Error", "All guest information fields are required.", "error");
      return;
    }
    setLoading(true);
    try {
      // Construct booking details for PayPal
      const bookingDetails = {
        bookingSidebar,
        userid: currentUser ? currentUser._id : "guest",
        totalamount,
        totaldays,
        guestName: name,
        guestEmail: email,
        guestPhoneNumber: phone,
        guestNationality: nationality,
        specialRequest: request,
        paymentMethod: "paypal",
        orderId, // Include the PayPal order ID
      };

      console.log("PayPal Booking Details sent to backend:", bookingDetails);

      // Send the booking details to the backend
      const response = await axios.post(
        "/api/payment/capturepaypalorder",
        bookingDetails
      );
      Swal.fire({
        title: "Success",
        text: "Your reservation was booked successfully",
        icon: "success",
        confirmButtonText: "OK",
      }).then(async () => {
        // Use provider hotel info instead of fetching
        const hotelInfo = ctxHotelInfo;
        navigate("/confirmation", {
          state: { booking: response.data.booking, hotelInfo, childrenAgeRule },
        });
        if (response.data.confirmationUrl) {
          window.location.href = response.data.confirmationUrl; // Redirect to confirmation page
        }
      });
    } catch (error) {
      console.error("Error capturing PayPal order:", error);
      Swal.fire("Payment failed", "Unable to complete the booking.", "error");
    } finally {
      setLoading(false);
    }
  };

  return {
    handlePayPalOrder,
    guestNameRef,
    guestEmailRef,
    guestPhoneNumberRef,
    guestNationalityRef,
    specialRequestRef,
    loading,
    setLoading,
    totalamount,
    totaldays,
  };
};
